import { ChangeDetectionStrategy, Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { I18nService, TranslatePipe } from 'i18n';

import { FunnelService } from '../../../services/funnel.service';
import { FunnelShellComponent } from '../funnel-shell/funnel-shell.component';

/**
 * Consumes a magic-link token (?token=...), starts/resumes a quiz session,
 * stores the session JWT, and redirects either into the wizard (status=started)
 * or to the result page (status=completed/purchased).
 */
@Component({
  selector: 'app-funnel-quiz-start-page',
  standalone: true,
  imports: [TranslatePipe, FunnelShellComponent],
  templateUrl: './funnel-quiz-start-page.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FunnelQuizStartPageComponent {
  private route      = inject(ActivatedRoute);
  private router     = inject(Router);
  private funnel     = inject(FunnelService);
  private i18n       = inject(I18nService);
  private destroyRef = inject(DestroyRef);

  loading = signal(true);
  error   = signal<string | null>(null);

  constructor() {
    const token = this.route.snapshot.queryParamMap.get('token')
               ?? this.route.snapshot.params['token']
               ?? '';

    if (!token) {
      this.loading.set(false);
      this.error.set('Missing confirmation token.');
      return;
    }

    this.funnel.startQuiz(token)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: session => {
          if (session.session_token) this.funnel.storeSessionToken(session.session_token);
          const lang = this.i18n.getLanguage() || 'en';
          const dest = (session.status === 'completed' || session.status === 'purchased')
            ? ['/', lang, 'quiz', 'result']
            : ['/', lang, 'quiz'];
          this.router.navigate(dest, { replaceUrl: true });
        },
        error: err => {
          this.loading.set(false);
          this.error.set(err?.error?.error || 'Your link is invalid or has expired.');
        }
      });
  }
}
